import type SubtitleCue from './subtitleCue';
import type TranscriptWord from './transcriptWord';
import type TranscriptParametricRule from './transcriptParametricRule';
import difference from 'lodash/difference';

export interface TranscriptRuleSetting {
  property: string;
  label: string;
  description?: string;
  isActive: boolean;
  min?: number;
  max?: number;
  value?: number;
}

export enum ParagraphRuleProperties {
  MAX_DURATION = 'maxDuration',
  MAX_CHARACTERS = 'maxCharacters',
  MAX_WORDS = 'maxWords',
  MAX_SILENCE = 'maxSilence',
  PUNCTUATION = 'punctuation',
  SPEAKER = 'speaker',
}

export enum SubtitleRuleProperties {
  MAX_DURATION = 'maxDuration',
  MAX_CHARACTERS = 'maxCharacters',
  MAX_WORDS = 'maxWords',
  MAX_SILENCE = 'maxSilence',
  LINE_BREAK_MAX_CHARACTERS = 'lineBreakMaxCharacters',
  PUNCTUATION = 'punctuation',
  SPEAKER = 'speaker',
}

/**
 * Adding a property to the following 2 lists will automtically add a new settings in the interface
 * and save the property in tenant config
*/
export const paragraphRulesSettings: TranscriptRuleSetting[] = [
  {
    property: ParagraphRuleProperties.MAX_DURATION,
    label: 'TranscriptParametricRules.ParagraphMaxDuration',
    description: 'TranscriptParametricRules.ParagraphMaxDurationTooltip',
    min: 1,
    max: 60000,
    value: 60000,
    isActive: true,
  },
  {
    property: ParagraphRuleProperties.MAX_CHARACTERS,
    label: 'TranscriptParametricRules.ParagraphMaxCharacters',
    description: 'TranscriptParametricRules.ParagraphMaxCharactersTooltip',
    min: 1,
    max: 1000,
    value: 500,
    isActive: false,
  },
  {
    property: ParagraphRuleProperties.MAX_WORDS,
    label: 'TranscriptParametricRules.ParagraphMaxWords',
    description: 'TranscriptParametricRules.ParagraphMaxWordsTooltip',
    min: 1,
    max: 100,
    value: 50,
    isActive: false,
  },
  {
    property: ParagraphRuleProperties.MAX_SILENCE,
    label: 'TranscriptParametricRules.ParagraphMaxSilence',
    description: 'TranscriptParametricRules.ParagraphMaxSilenceTooltip',
    min: 1,
    max: 5000,
    value: 800,
    isActive: true,
  },
  {
    property: ParagraphRuleProperties.PUNCTUATION,
    label: 'TranscriptParametricRules.ParagraphPunctuation',
    description: 'TranscriptParametricRules.ParagraphPunctuationTooltip',
    isActive: false,
  },
  {
    property: ParagraphRuleProperties.SPEAKER,
    label: 'TranscriptParametricRules.ParagraphSpeaker',
    description: 'TranscriptParametricRules.ParagraphSpeakerTooltip',
    isActive: false,
  },
];

export const subtitleRulesSettings: TranscriptRuleSetting[] = [
  {
    property: SubtitleRuleProperties.MAX_DURATION,
    label: 'TranscriptParametricRules.SubtitleMaxDuration',
    description: 'TranscriptParametricRules.SubtitleMaxDurationTooltip',
    min: 1,
    max: 30000,
    value: 3000,
    isActive: false,
  },
  {
    property: SubtitleRuleProperties.MAX_CHARACTERS,
    label: 'TranscriptParametricRules.SubtitleMaxCharacters',
    description: 'TranscriptParametricRules.SubtitleMaxCharactersTooltip',
    min: 1,
    max: 200,
    value: 100,
    isActive: false,
  },
  {
    property: SubtitleRuleProperties.MAX_WORDS,
    label: 'TranscriptParametricRules.SubtitleMaxWords',
    description: 'TranscriptParametricRules.SubtitleMaxWordsTooltip',
    min: 1,
    max: 20,
    value: 8,
    isActive: true,
  },
  {
    property: SubtitleRuleProperties.MAX_SILENCE,
    label: 'TranscriptParametricRules.SubtitleMaxSilence',
    description: 'TranscriptParametricRules.SubtitleMaxSilenceTooltip',
    min: 1,
    max: 5000,
    value: 800,
    isActive: true,
  },
  {
    property: SubtitleRuleProperties.LINE_BREAK_MAX_CHARACTERS,
    label: 'TranscriptParametricRules.SubtitleLineBreak',
    description: 'TranscriptParametricRules.SubtitleLineBreakTooltip',
    min: 1,
    max: 200,
    value: 50,
    isActive: false,
  },
  {
    property: SubtitleRuleProperties.PUNCTUATION,
    label: 'TranscriptParametricRules.SubtitlePunctuation',
    description: 'TranscriptParametricRules.SubtitlePunctuationTooltip',
    isActive: true,
  },
  {
    property: SubtitleRuleProperties.SPEAKER,
    label: 'TranscriptParametricRules.SubtitleSpeaker',
    description: 'TranscriptParametricRules.SubtitleSpeakerTooltip',
    isActive: false,
  },
];

const getProperty = <T, K extends keyof T>(o: T, propertyName: K): T[K] => {
  return o[propertyName];
};

const maxCharacterCheckRule = (
    currentSubtitleCue: SubtitleCue,
    prev: TranscriptWord,
    cur: TranscriptWord,
    value: number,
) => {
  const length =
      currentSubtitleCue.getCharacterLength() + cur.content.length;
  return length >= value;
};

const checkCallbacks = {
      maxDuration: {
        checkRule: (
          currentSubtitleCue: SubtitleCue,
          prev: TranscriptWord,
          cur: TranscriptWord,
          value: number,
        ) => {
          if (!currentSubtitleCue.words.length) return false;
          const duration = cur.endTime - currentSubtitleCue.words[0].startTime;
          return duration >= value;
        },
        formattedValue: (value: number) => `${(value / 1000).toFixed(2)} s`,
      },
      maxWords: {
        checkRule: (
          currentSubtitleCue: SubtitleCue,
          prev: TranscriptWord,
          cur: TranscriptWord,
          value: number,
        ) => {
          return currentSubtitleCue.words.length >= value;
        },
        formattedValue: (value: number) => `${value}`,
      },
      maxCharacters: {
        checkRule: maxCharacterCheckRule,
        formattedValue: (value: number) => `${value}`,
      },
      maxSilence: {
        checkRule: (
          currentSubtitleCue: SubtitleCue,
          prev: TranscriptWord,
          cur: TranscriptWord,
          value: number,
        ) => {
          return cur.startTime - prev.endTime >= value;
        },
        formattedValue: (value: number) => `${value / 1000} s`,
      },
      lineBreakMaxCharacters: {
        checkRule: maxCharacterCheckRule,
        formattedValue: (value: number) => `${value}`,
      },
      punctuation: {
        checkRule: (
          currentSubtitleCue: SubtitleCue,
          prev: TranscriptWord,
          cur: TranscriptWord,
          value: number,
        ) => {
          return /[.?!]$/.test(prev.content);
        }
      },
      speaker: {
        checkRule: (
          currentSubtitleCue: SubtitleCue,
          prev: TranscriptWord,
          cur: TranscriptWord,
          value: number,
        ) => {
          return prev.speaker !== cur.speaker;
        }
      },
};

export const getParametricRules = (ruleSettings: TranscriptRuleSetting[], values: any = undefined): TranscriptParametricRule[] => {
  const rules: TranscriptParametricRule[] = [];
  ruleSettings.forEach((setting: any) => {
    const prop = values ? getProperty(values, setting.property) : undefined;
    const isActive = prop ? prop.isActive : setting.isActive;
    const value = prop ? prop.value : setting.value;
    const callbacks = getProperty(checkCallbacks, setting.property);
    if (callbacks) {
      const rule = {
          property: setting.property,
          isActive: isActive,
          label: setting.label,
          description: setting.description,
          value: value,
          min: setting.min,
          max: setting.max,
          checkRule: callbacks.checkRule,
          formattedValue: callbacks.formattedValue,
      };
      rules.push(rule);
    }
  });
  return rules;
};

export const getMergedRuleSettings = (tenantConfigSettings: TranscriptRuleSetting[] | undefined, defaultSettings: TranscriptRuleSetting[], propertyList: string[]) => {
  if (!tenantConfigSettings) {
    return {
      hasUpdated: true,
      mergedSettings: [...defaultSettings],
    };
  }

  // Merge descriptions from default settings into tenant settings
  const settingsWithDescriptions = tenantConfigSettings.map(tenantSetting => {
    const defaultSetting = defaultSettings.find(ds => ds.property === tenantSetting.property);
    if (defaultSetting?.description && !tenantSetting.description) {
      return { ...tenantSetting, description: defaultSetting.description };
    }
    return tenantSetting;
  });

  const modifiedSettings = addMissingProperties(settingsWithDescriptions, defaultSettings, propertyList);
  if (modifiedSettings === null) {
    return {
      hasUpdated: false,
      mergedSettings: settingsWithDescriptions,
    };
  }

  return {
    hasUpdated: true,
    mergedSettings: modifiedSettings,
  };
};

const addMissingProperties = (settingsObject: TranscriptRuleSetting[], objectsToChooseFrom: TranscriptRuleSetting[], propertyList: string[]) => {
  const presentProperties = settingsObject.map((currentObject) => currentObject.property);
  const missingProperties = difference(propertyList, presentProperties);

  if (missingProperties.length < 1) {
    return null;
  }

  const modifiedSettingsObject = [...settingsObject];
  objectsToChooseFrom.forEach((currentObject) => {
    if (missingProperties.includes(currentObject.property)) {
      modifiedSettingsObject.push(currentObject);
    }
  });

  return modifiedSettingsObject;
};
